import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "./firebase";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  Activity, 
  User, 
  FileText, 
  Clock, 
  Plus, 
  X, 
  Save, 
  ArrowRight,
  AlertCircle,
  CheckCircle,
  Stethoscope,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Award,
  BookOpen,
  Star,
  Navigation,
  ExternalLink
} from "lucide-react";

// Fix Leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

/**
 * Doctor Profile Setup Component with Leaflet Maps Integration
 * 
 * SETUP REQUIREMENTS:
 * 1. Install: npm install leaflet react-leaflet
 * 2. Firebase Firestore Rules:
 *    rules_version = '2';
 *    service cloud.firestore {
 *      match /databases/{database}/documents {
 *        match /doctors/{userId} {
 *          allow read, write: if request.auth != null && request.auth.uid == userId;
 *        }
 *        match /users/{userId} {
 *          allow read, write: if request.auth != null && request.auth.uid == userId;
 *        }
 *      }
 *    }
 * 3. Create Firestore indexes if needed (Firebase will prompt you with URLs)
 * 4. No API keys required - uses free OpenStreetMap!
 */

// Component to handle map updates when position changes externally
function MapUpdater({ position }) {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.flyTo(position, 13);
    }
  }, [position, map]);
  
  return null;
}

// Component to handle map clicks and marker dragging
function LocationMarker({ position, setPosition, updateLocation }) {
  const map = useMapEvents({
    click(e) {
      const newPos = [e.latlng.lat, e.latlng.lng];
      setPosition(newPos);
      updateLocation(e.latlng.lat, e.latlng.lng);
    },
  });

  return position === null ? null : (
    <Marker 
      position={position}
      draggable={true}
      eventHandlers={{
        dragend: (e) => {
          const marker = e.target;
          const newPos = marker.getLatLng();
          const newPosition = [newPos.lat, newPos.lng];
          setPosition(newPosition);
          updateLocation(newPos.lat, newPos.lng);
        },
      }}
    />
  );
}

const DoctorProfileSetup = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Map state
  const [mapPosition, setMapPosition] = useState([40.7128, -74.0060]); // Default to NYC
  const [searchQuery, setSearchQuery] = useState("");
  const [locationLoading, setLocationLoading] = useState(false);
  
  // Profile data
  const [profileData, setProfileData] = useState({
    name: "",
    specialty: "",
    bio: "",
    experience: "",
    education: "",
    phone: "",
    location: {
      address: "",
      coordinates: {
        lat: 40.7128,
        lng: -74.0060
      },
      placeId: ""
    },
    calendarIntegration: {
      type: "",
      isConnected: false,
      calendarId: ""
    },
    workingHours: {
      monday: { enabled: true, start: "09:00", end: "17:00" },
      tuesday: { enabled: true, start: "09:00", end: "17:00" },
      wednesday: { enabled: true, start: "09:00", end: "17:00" },
      thursday: { enabled: true, start: "09:00", end: "17:00" },
      friday: { enabled: true, start: "09:00", end: "17:00" },
      saturday: { enabled: false, start: "09:00", end: "17:00" },
      sunday: { enabled: false, start: "09:00", end: "17:00" }
    },
    startedPracticeYear: "",
    languages: [],
    certifications: [],
    services: []
  });
  
  // Form helpers
  const [serviceForm, setServiceForm] = useState({
    title: "",
    description: "",
    price: "",
    duration: ""
  });
  
  const [newLanguage, setNewLanguage] = useState("");
  const [newCertification, setNewCertification] = useState("");
  const [currentStep, setCurrentStep] = useState(1);

  const navigate = useNavigate();

  // Modern styles (keeping the same styles object)
  const styles = {
    container: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f0f9ff 0%, #ffffff 30%, #f0fdf4 100%)",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    },
    header: {
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      backdropFilter: "blur(20px)",
      borderBottom: "1px solid rgba(229, 231, 235, 0.5)",
      padding: "24px 0",
      marginBottom: "32px"
    },
    headerContent: {
      maxWidth: "800px",
      margin: "0 auto",
      padding: "0 24px",
      display: "flex",
      alignItems: "center",
      gap: "16px"
    },
    logoContainer: {
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      padding: "12px",
      borderRadius: "16px",
      boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.3)"
    },
    headerTitle: {
      fontSize: "24px",
      fontWeight: "700",
      background: "linear-gradient(135deg, #1f2937, #6b7280)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      margin: 0
    },
    headerSubtitle: {
      fontSize: "16px",
      color: "#6b7280",
      margin: 0
    },
    mainContent: {
      maxWidth: "800px",
      margin: "0 auto",
      padding: "0 24px 48px 24px"
    },
    card: {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(20px)",
      borderRadius: "24px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      overflow: "hidden"
    },
    stepIndicator: {
      padding: "32px 48px",
      background: "linear-gradient(135deg, #f8fafc, #f1f5f9)",
      borderBottom: "1px solid rgba(229, 231, 235, 0.3)"
    },
    stepRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "16px"
    },
    stepItem: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 16px",
      borderRadius: "12px",
      transition: "all 0.2s ease"
    },
    stepItemActive: {
      backgroundColor: "#dbeafe",
      color: "#1d4ed8"
    },
    stepItemCompleted: {
      backgroundColor: "#dcfce7",
      color: "#166534"
    },
    stepNumber: {
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "12px",
      fontWeight: "600"
    },
    formSection: {
      padding: "48px"
    },
    sectionTitle: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#111827",
      marginBottom: "24px",
      display: "flex",
      alignItems: "center",
      gap: "12px"
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "24px",
      marginBottom: "32px"
    },
    inputGroup: {
      marginBottom: "24px"
    },
    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: "500",
      color: "#374151",
      marginBottom: "8px"
    },
    input: {
      width: "100%",
      padding: "16px",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      fontSize: "16px",
      transition: "all 0.2s ease",
      backgroundColor: "#f9fafb",
      boxSizing: "border-box",
      outline: "none"
    },
    textarea: {
      width: "100%",
      padding: "16px",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      fontSize: "16px",
      transition: "all 0.2s ease",
      backgroundColor: "#f9fafb",
      boxSizing: "border-box",
      outline: "none",
      resize: "vertical",
      minHeight: "120px"
    },
    select: {
      width: "100%",
      padding: "16px",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      fontSize: "16px",
      transition: "all 0.2s ease",
      backgroundColor: "#f9fafb",
      boxSizing: "border-box",
      outline: "none"
    },
    mapContainer: {
      width: "100%",
      height: "400px",
      borderRadius: "12px",
      overflow: "hidden",
      border: "2px solid #e5e7eb",
      backgroundColor: "#f3f4f6"
    },
    calendarSection: {
      backgroundColor: "#f8fafc",
      borderRadius: "12px",
      padding: "24px",
      border: "1px solid #e2e8f0"
    },
    calendarOption: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "16px",
      borderRadius: "8px",
      border: "2px solid #e5e7eb",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginBottom: "12px"
    },
    calendarOptionSelected: {
      borderColor: "#3b82f6",
      backgroundColor: "#dbeafe"
    },
    timeGrid: {
      display: "grid",
      gridTemplateColumns: "120px 1fr 1fr",
      gap: "12px",
      alignItems: "center",
      marginBottom: "12px"
    },
    dayLabel: {
      fontSize: "14px",
      fontWeight: "500",
      color: "#374151"
    },
    timeInput: {
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "14px"
    },
    checkbox: {
      width: "16px",
      height: "16px",
      marginRight: "8px"
    },
    addItemRow: {
      display: "flex",
      gap: "12px",
      alignItems: "end",
      marginBottom: "16px"
    },
    addButton: {
      padding: "16px 24px",
      background: "linear-gradient(135deg, #10b981, #059669)",
      color: "white",
      border: "none",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      whiteSpace: "nowrap"
    },
    tagList: {
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
      marginTop: "12px"
    },
    tag: {
      padding: "8px 12px",
      backgroundColor: "#dbeafe",
      color: "#1e40af",
      borderRadius: "20px",
      fontSize: "14px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "8px"
    },
    removeTag: {
      background: "none",
      border: "none",
      color: "#1e40af",
      cursor: "pointer",
      padding: "2px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    serviceCard: {
      padding: "20px",
      backgroundColor: "#f8fafc",
      borderRadius: "12px",
      border: "1px solid #e2e8f0",
      marginBottom: "16px"
    },
    serviceHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "start",
      marginBottom: "8px"
    },
    serviceTitle: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#111827",
      margin: 0
    },
    servicePrice: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#059669",
      backgroundColor: "#dcfce7",
      padding: "4px 8px",
      borderRadius: "6px"
    },
    serviceDescription: {
      fontSize: "14px",
      color: "#6b7280",
      margin: "8px 0"
    },
    serviceMeta: {
      fontSize: "12px",
      color: "#9ca3af",
      display: "flex",
      alignItems: "center",
      gap: "16px"
    },
    alert: {
      padding: "16px",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "24px"
    },
    errorAlert: {
      backgroundColor: "#fee2e2",
      border: "1px solid #fecaca",
      color: "#dc2626"
    },
    successAlert: {
      backgroundColor: "#dcfce7",
      border: "1px solid #bbf7d0",
      color: "#16a34a"
    },
    buttonRow: {
      display: "flex",
      gap: "16px",
      justifyContent: "end",
      marginTop: "32px",
      paddingTop: "32px",
      borderTop: "1px solid #e5e7eb"
    },
    button: {
      padding: "16px 24px",
      borderRadius: "12px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      border: "none"
    },
    primaryButton: {
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      color: "white",
      boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.3)"
    },
    secondaryButton: {
      backgroundColor: "#f3f4f6",
      color: "#374151",
      border: "1px solid #d1d5db"
    },
    spinner: {
      width: "20px",
      height: "20px",
      border: "2px solid transparent",
      borderTop: "2px solid white",
      borderRadius: "50%",
      animation: "spin 1s linear infinite"
    }
  };
const specialties = [
  "Mjekësi e përgjithshme","Mjekësi familjare","Mjekësi interne","Kardiologji","Kardiokirurgji","Pneumologji","Gastroenterologji","Hepatologji","Endokrinologji","Diabetologji","Nefrologji","Hematologji","Reumatologji","Sëmundje infektive","Alergologji dhe Imunologji","Neurologji","Neurokirurgji","Dermatologji","Oftalmologji","ORL (Otorinolaringologji)","Gjinekologji & Obstetrikë","Urologji","Onkologji","Radiologji","Radiologji ndërhyrëse","Anesteziologji dhe Reanimacion","Patologji (Anatomo-patologji)","Mjekësi e urgjencës","Mjekësi e punës","Gjenetikë Mjekësore","Mjekësi sportive","Mjekësi fizike dhe Rehabilitim","Ortopedi","Kirurgji e përgjithshme","Kirurgji plastike & rikonstruktive","Kirurgji Maksilofaciale","Kirurgji torakale","Kirurgji vaskulare","Kirurgji pediatrike","Pediatri","Neonatologji","Geriatri","Psikologji","Psikiatri","Psikiatri e fëmijëve & adoleshentëve","Logopedi","Dietologji / Nutricionizëm","Mjekësi laboratorike","Mikrobiologji","Infermier","Dentist","Fizioterapist"
];


  const experienceLevels = [
    "Less than 1 year", "1-3 years", "3-5 years", 
    "5-10 years", "10-15 years", "15+ years"
  ];

  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  const calendarOptions = [
    {
      type: 'google',
      name: 'Google Calendar',
      description: 'Sync with your Google Calendar for automatic scheduling',
      icon: '📅'
    },
    {
      type: 'outlook',
      name: 'Microsoft Outlook',
      description: 'Connect with Outlook Calendar for seamless integration',
      icon: '📆'
    },
    {
      type: 'manual',
      name: 'Manual Scheduling',
      description: 'Manage appointments manually through our platform',
      icon: '📋'
    }
  ];

  // Initialize Firebase auth and load existing profile
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadExistingProfile(currentUser.uid);
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Update map position when profile data changes
  useEffect(() => {
    if (profileData.location.coordinates) {
      setMapPosition([profileData.location.coordinates.lat, profileData.location.coordinates.lng]);
    }
  }, [profileData.location.coordinates]);

  // Auto-clear success/error messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Reverse geocoding using free Nominatim service (OpenStreetMap)
  const reverseGeocode = async (lat, lng) => {
    try {
      setError(""); // Clear any previous errors
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
      );
      const data = await response.json();
      
      if (data && data.display_name) {
        const locationData = {
          address: data.display_name,
          coordinates: { lat, lng },
          placeId: data.place_id || ''
        };
        
        updateProfileData("location", locationData);
        setSearchQuery(data.display_name); // Update search input
        setSuccess("Location updated successfully!");
        
        console.log('Location updated via reverse geocoding:', locationData);
      }
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      // Fallback to coordinates display
      const fallbackAddress = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      const locationData = {
        address: fallbackAddress,
        coordinates: { lat, lng },
        placeId: ''
      };
      
      updateProfileData("location", locationData);
      setSearchQuery(fallbackAddress);
      console.log('Location updated with coordinates fallback:', locationData);
    }
  };

  // Forward geocoding for address search
  const searchAddress = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setError("Please enter an address to search");
      return;
    }
    
    setError(""); // Clear previous errors
    setLocationLoading(true);
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1&addressdetails=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);
        
        // Update both map position and profile data
        setMapPosition([lat, lng]);
        updateProfileData("location", {
          address: result.display_name,
          coordinates: { lat, lng },
          placeId: result.place_id || ''
        });
        
        setSuccess('Location found and updated!');
      } else {
        setError('Address not found. Please try a different search term or click on the map.');
      }
    } catch (error) {
      console.error('Address search failed:', error);
      setError('Failed to search address. Please try again or click on the map.');
    } finally {
      setLocationLoading(false);
    }
  };

  const loadExistingProfile = async (uid) => {
    try {
      const doctorDoc = await getDoc(doc(db, "doctors", uid));
      if (doctorDoc.exists()) {
        const data = doctorDoc.data();
        const loadedData = {
          name: data.name || "",
          specialty: data.specialty || "",
          bio: data.bio || "",
          experience: data.experience || "",
          education: data.education || "",
          phone: data.phone || "",
          location: data.location || {
            address: "",
            coordinates: { lat: 40.7128, lng: -74.0060 },
            placeId: ""
          },
          calendarIntegration: data.calendarIntegration || {
            type: "",
            isConnected: false,
            calendarId: ""
          },
          workingHours: data.workingHours || {
            monday: { enabled: true, start: "09:00", end: "17:00" },
            tuesday: { enabled: true, start: "09:00", end: "17:00" },
            wednesday: { enabled: true, start: "09:00", end: "17:00" },
            thursday: { enabled: true, start: "09:00", end: "17:00" },
            friday: { enabled: true, start: "09:00", end: "17:00" },
            saturday: { enabled: false, start: "09:00", end: "17:00" },
            sunday: { enabled: false, start: "09:00", end: "17:00" }
          },
          startedPracticeYear: data.startedPracticeYear || "",
          languages: data.languages || [],
          certifications: data.certifications || [],
          services: data.services || []
        };
        
        setProfileData(loadedData);
        
        // Set search query to existing address
        if (loadedData.location.address) {
          setSearchQuery(loadedData.location.address);
        }
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      if (error.code === 'failed-precondition') {
        console.warn("Firestore index required. This is expected for new projects.");
      } else {
        setError("Failed to load existing profile data.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setLocationLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          setMapPosition([lat, lng]);
          reverseGeocode(lat, lng);
          setLocationLoading(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          setError("Unable to get your current location. Please search manually or click on the map.");
          setLocationLoading(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    } else {
      setError("Geolocation is not supported by this browser.");
    }
  };

  const updateProfileData = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateWorkingHours = (day, field, value) => {
    setProfileData(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: {
          ...prev.workingHours[day],
          [field]: value
        }
      }
    }));
  };

  const connectCalendar = (type) => {
    updateProfileData("calendarIntegration", {
      type: type,
      isConnected: false,
      calendarId: ""
    });

    if (type !== 'manual') {
      setSuccess(`${type === 'google' ? 'Google' : 'Outlook'} Calendar integration selected.`);
    }
  };

  const addLanguage = () => {
    if (newLanguage.trim() && !profileData.languages.includes(newLanguage.trim())) {
      updateProfileData("languages", [...profileData.languages, newLanguage.trim()]);
      setNewLanguage("");
    }
  };

  const removeLanguage = (language) => {
    updateProfileData("languages", profileData.languages.filter(l => l !== language));
  };

  const addCertification = () => {
    if (newCertification.trim() && !profileData.certifications.includes(newCertification.trim())) {
      updateProfileData("certifications", [...profileData.certifications, newCertification.trim()]);
      setNewCertification("");
    }
  };

  const removeCertification = (certification) => {
    updateProfileData("certifications", profileData.certifications.filter(c => c !== certification));
  };

  const addService = () => {
    if (serviceForm.title.trim() && serviceForm.description.trim()) {
      const newService = {
        title: serviceForm.title.trim(),
        description: serviceForm.description.trim(),
        price: serviceForm.price.trim(),
        duration: serviceForm.duration.trim(),
        id: Date.now().toString()
      };
      
      updateProfileData("services", [...profileData.services, newService]);
      setServiceForm({ title: "", description: "", price: "", duration: "" });
    }
  };

  const removeService = (serviceId) => {
    updateProfileData("services", profileData.services.filter(s => s.id !== serviceId));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return profileData.name.trim() && profileData.specialty && profileData.startedPracticeYear;
      case 2:
        // Step 2 validation: Need bio filled out
        // Location is considered set if:
        // 1. Address has been entered/searched, OR
        // 2. Map position has been changed from default NYC coordinates, OR
        // 3. User has clicked on map or dragged marker (coordinates changed)
        const hasBio = profileData.bio.trim().length > 0;
        const hasAddress = profileData.location.address.trim().length > 0;
        const hasCustomLocation = Math.abs(profileData.location.coordinates.lat - 40.7128) > 0.001 || 
                                 Math.abs(profileData.location.coordinates.lng + 74.0060) > 0.001;
        
        console.log('Step 2 validation:', {
          hasBio,
          hasAddress,
          hasCustomLocation,
          bio: profileData.bio,
          address: profileData.location.address,
          coordinates: profileData.location.coordinates
        });
        
        return hasBio && (hasAddress || hasCustomLocation);
      case 3:
        return profileData.calendarIntegration.type;
      case 4:
        return profileData.services.length > 0;
      default:
        return true;
    }
  };

  const saveProfile = async () => {
    if (!user) return;
    
    // Validate required fields
    if (!profileData.name || !profileData.specialty || !profileData.startedPracticeYear) {
      setError("Please fill in all required fields (Name, Specialty, Years of Practice).");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      // Save to doctors collection
      await setDoc(doc(db, "doctors", user.uid), {
        ...profileData,
        email: user.email,
        uid: user.uid,
        updatedAt: new Date(),
        createdAt: new Date(),
        profileComplete: true,
        isVerified: false
      }, { merge: true });

      // Update user document
      await setDoc(doc(db, "users", user.uid), {
        name: profileData.name,
        email: user.email,
        role: "doctor",
        profileComplete: true,
        updatedAt: new Date()
      }, { merge: true });

      setSuccess("Profile saved successfully! Redirecting to verification...");
      
      setTimeout(() => {
        navigate("/verify-doctor");
      }, 2000);

    } catch (error) {
      console.error("Error saving profile:", error);
      setError("Failed to save profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = "#3b82f6";
    e.target.style.backgroundColor = "white";
    e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
    e.target.style.border = "2px solid #3b82f6";
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = "#e5e7eb";
    e.target.style.backgroundColor = "#f9fafb";
    e.target.style.boxShadow = "none";
    e.target.style.border = "2px solid #e5e7eb";
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
          <div style={styles.spinner}></div>
        </div>
      </div>
    );
  }

  const steps = [
    { number: 1, title: "Basic Info", icon: User },
    { number: 2, title: "Location & Bio", icon: MapPin },
    { number: 3, title: "Calendar Setup", icon: Calendar },
    { number: 4, title: "Services", icon: BookOpen }
  ];

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .primary-button:hover:not(:disabled) {
          background: linear-gradient(135deg, #1d4ed8, #4338ca) !important;
          transform: translateY(-2px);
          box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.4) !important;
        }
        
        .secondary-button:hover {
          background-color: #e5e7eb !important;
        }
        
        .add-button:hover:not(:disabled) {
          background: linear-gradient(135deg, #059669, #047857) !important;
          transform: translateY(-1px);
        }
        
        .add-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .remove-tag:hover {
          background-color: rgba(30, 64, 175, 0.1) !important;
        }
        
        .calendar-option:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
        }
        
        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr !important;
          }
          
          .step-row {
            flex-direction: column !important;
            gap: 8px !important;
          }
          
          .button-row {
            flex-direction: column !important;
          }
          
          .add-item-row {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          .time-grid {
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }
        }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logoContainer}>
            <Activity style={{ color: "white", width: "24px", height: "24px" }} />
          </div>
          <div>
            <h1 style={styles.headerTitle}>Doctor Profile Setup</h1>
            <p style={styles.headerSubtitle}>Complete your professional profile</p>
          </div>
        </div>
      </div>

      <div style={styles.mainContent}>
        <div style={styles.card}>
          {/* Step Indicator */}
          <div style={styles.stepIndicator}>
            <div style={styles.stepRow} className="step-row">
              {steps.map((step) => {
                const Icon = step.icon;
                const isActive = currentStep === step.number;
                const isCompleted = validateStep(step.number);
                
                return (
                  <div 
                    key={step.number}
                    style={{
                      ...styles.stepItem,
                      ...(isActive ? styles.stepItemActive : {}),
                      ...(isCompleted && !isActive ? styles.stepItemCompleted : {})
                    }}
                  >
                    <div style={{
                      ...styles.stepNumber,
                      backgroundColor: isActive ? "#1d4ed8" : isCompleted ? "#166534" : "#e5e7eb",
                      color: isActive || isCompleted ? "white" : "#6b7280"
                    }}>
                      {isCompleted && !isActive ? (
                        <CheckCircle style={{ width: "12px", height: "12px" }} />
                      ) : (
                        step.number
                      )}
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: "500" }}>
                      {step.title}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form Content */}
          <div style={styles.formSection}>
            {/* Alert Messages */}
            {error && (
              <div style={{...styles.alert, ...styles.errorAlert}}>
                <AlertCircle style={{ width: "16px", height: "16px" }} />
                {error}
              </div>
            )}

            {success && (
              <div style={{...styles.alert, ...styles.successAlert}}>
                <CheckCircle style={{ width: "16px", height: "16px" }} />
                {success}
              </div>
            )}

            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <>
                <h2 style={styles.sectionTitle}>
                  <User style={{ width: "24px", height: "24px", color: "#3b82f6" }} />
                  Basic Information
                </h2>
                
                <div style={styles.formGrid} className="form-grid">
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Full Name *</label>
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="Dr. John Smith"
                      value={profileData.name}
                      onChange={(e) => updateProfileData("name", e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      required
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Medical Specialty *</label>
                    <select
                      style={styles.select}
                      value={profileData.specialty}
                      onChange={(e) => updateProfileData("specialty", e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      required
                    >
                      <option value="">Select specialty</option>
                      {specialties.map(specialty => (
                        <option key={specialty} value={specialty}>{specialty}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Year Started Practice *</label>
                    <input
                      type="number"
                      style={styles.input}
                      placeholder="2015"
                      min="1970"
                      max={new Date().getFullYear()}
                      value={profileData.startedPracticeYear}
                      onChange={(e) => updateProfileData("startedPracticeYear", e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      required
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Experience Level</label>
                    <select
                      style={styles.select}
                      value={profileData.experience}
                      onChange={(e) => updateProfileData("experience", e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    >
                      <option value="">Select experience</option>
                      {experienceLevels.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Phone Number</label>
                    <input
                      type="tel"
                      style={styles.input}
                      placeholder="+1 (555) 123-4567"
                      value={profileData.phone}
                      onChange={(e) => updateProfileData("phone", e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Step 2: Location & Professional Details */}
            {currentStep === 2 && (
              <>
                <h2 style={styles.sectionTitle}>
                  <MapPin style={{ width: "24px", height: "24px", color: "#10b981" }} />
                  Location & Professional Details
                </h2>

                {/* Location Section */}
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Practice Location *</label>
                  <div style={styles.addItemRow} className="add-item-row">
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="Search for your practice address..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          searchAddress(searchQuery);
                        }
                      }}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                    <button
                      type="button"
                      style={{
                        ...styles.addButton,
                        ...(locationLoading ? { opacity: 0.7 } : {})
                      }}
                      className="add-button"
                      onClick={() => searchAddress(searchQuery)}
                      disabled={locationLoading}
                    >
                      {locationLoading ? (
                        <div style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid transparent",
                          borderTop: "2px solid white",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite"
                        }}></div>
                      ) : (
                        "🔍"
                      )}
                      {locationLoading ? "Searching..." : "Search"}
                    </button>
                    <button
                      type="button"
                      style={{
                        ...styles.addButton,
                        ...(locationLoading ? { opacity: 0.7 } : {})
                      }}
                      className="add-button"
                      onClick={getCurrentLocation}
                      disabled={locationLoading}
                    >
                      <Navigation style={{ width: "16px", height: "16px" }} />
                      Use Current
                    </button>
                  </div>
                  
                  {profileData.location.address && (
                    <div style={{
                      marginTop: "8px",
                      padding: "12px",
                      backgroundColor: "#f0f9ff",
                      borderRadius: "8px",
                      border: "1px solid #bae6fd",
                      fontSize: "14px",
                      color: "#0369a1"
                    }}>
                      📍 Current address: {profileData.location.address}
                    </div>
                  )}
                </div>

                {/* Leaflet Map Container */}
                <div style={styles.inputGroup}>
                  <div style={styles.mapContainer}>
                    <MapContainer
                      center={mapPosition}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                      key={`${mapPosition[0]}-${mapPosition[1]}`} // Force re-render when position changes
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <MapUpdater position={mapPosition} />
                      <LocationMarker 
                        position={mapPosition} 
                        setPosition={setMapPosition}
                        updateLocation={reverseGeocode}
                      />
                    </MapContainer>
                  </div>
                  <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "8px" }}>
                    📍 Click on the map to set your exact practice location. Drag the marker to fine-tune the position.
                  </p>
                  {profileData.location.coordinates && (
                    <div style={{ 
                      marginTop: "8px", 
                      fontSize: "12px", 
                      color: "#9ca3af",
                      fontFamily: "monospace",
                      backgroundColor: "#f3f4f6",
                      padding: "8px",
                      borderRadius: "6px"
                    }}>
                      Coordinates: {profileData.location.coordinates.lat.toFixed(6)}, {profileData.location.coordinates.lng.toFixed(6)}
                      <div style={{ marginTop: "4px", color: "#10b981", fontSize: "11px" }}>
                        ✅ Using free OpenStreetMap - no API key required!
                      </div>
                      <div style={{ marginTop: "4px", fontSize: "11px" }}>
                        Step 2 Valid: {validateStep(2) ? '✅ Yes' : '❌ No'} 
                        {validateStep(2) ? '' : ' (Need bio + location)'}
                      </div>
                    </div>
                  )}
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Professional Bio *</label>
                  <textarea
                    style={styles.textarea}
                    placeholder="Tell patients about your background, expertise, and approach to healthcare..."
                    value={profileData.bio}
                    onChange={(e) => updateProfileData("bio", e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    required
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Education & Training</label>
                  <textarea
                    style={styles.textarea}
                    placeholder="Medical school, residency, fellowships, etc."
                    value={profileData.education}
                    onChange={(e) => updateProfileData("education", e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </div>

                {/* Languages */}
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Languages Spoken</label>
                  <div style={styles.addItemRow} className="add-item-row">
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="Enter language"
                      value={newLanguage}
                      onChange={(e) => setNewLanguage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addLanguage();
                        }
                      }}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                    <button
                      type="button"
                      style={styles.addButton}
                      className="add-button"
                      onClick={addLanguage}
                      disabled={!newLanguage.trim()}
                    >
                      <Plus style={{ width: "16px", height: "16px" }} />
                      Add
                    </button>
                  </div>
                  
                  {profileData.languages.length > 0 && (
                    <div style={styles.tagList}>
                      {profileData.languages.map((language, index) => (
                        <div key={index} style={styles.tag}>
                          {language}
                          <button
                            style={styles.removeTag}
                            className="remove-tag"
                            onClick={() => removeLanguage(language)}
                          >
                            <X style={{ width: "12px", height: "12px" }} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Certifications */}
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Certifications & Licenses</label>
                  <div style={styles.addItemRow} className="add-item-row">
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="Enter certification"
                      value={newCertification}
                      onChange={(e) => setNewCertification(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCertification();
                        }
                      }}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                    <button
                      type="button"
                      style={styles.addButton}
                      className="add-button"
                      onClick={addCertification}
                      disabled={!newCertification.trim()}
                    >
                      <Plus style={{ width: "16px", height: "16px" }} />
                      Add
                    </button>
                  </div>
                  
                  {profileData.certifications.length > 0 && (
                    <div style={styles.tagList}>
                      {profileData.certifications.map((cert, index) => (
                        <div key={index} style={styles.tag}>
                          {cert}
                          <button
                            style={styles.removeTag}
                            className="remove-tag"
                            onClick={() => removeCertification(cert)}
                          >
                            <X style={{ width: "12px", height: "12px" }} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Step 3: Calendar Setup */}
            {currentStep === 3 && (
              <>
                <h2 style={styles.sectionTitle}>
                  <Calendar style={{ width: "24px", height: "24px", color: "#8b5cf6" }} />
                  Calendar Integration & Availability
                </h2>

                <div style={styles.calendarSection}>
                  <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "16px", color: "#111827" }}>
                    Choose Calendar Integration
                  </h3>
                  <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "24px" }}>
                    Select how you'd like to manage your appointments and availability
                  </p>

                  {calendarOptions.map((option) => (
                    <div
                      key={option.type}
                      style={{
                        ...styles.calendarOption,
                        ...(profileData.calendarIntegration.type === option.type ? styles.calendarOptionSelected : {})
                      }}
                      className="calendar-option"
                      onClick={() => connectCalendar(option.type)}
                    >
                      <span style={{ fontSize: "24px" }}>{option.icon}</span>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ fontSize: "16px", fontWeight: "600", margin: "0 0 4px 0", color: "#111827" }}>
                          {option.name}
                        </h4>
                        <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>
                          {option.description}
                        </p>
                      </div>
                      {profileData.calendarIntegration.type === option.type && (
                        <CheckCircle style={{ width: "20px", height: "20px", color: "#059669" }} />
                      )}
                    </div>
                  ))}

                  {profileData.calendarIntegration.type && profileData.calendarIntegration.type !== 'manual' && (
                    <div style={{
                      marginTop: "20px",
                      padding: "16px",
                      backgroundColor: "#dbeafe",
                      borderRadius: "8px",
                      border: "1px solid #93c5fd"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                        <ExternalLink style={{ width: "16px", height: "16px", color: "#1d4ed8" }} />
                        <span style={{ fontSize: "14px", fontWeight: "500", color: "#1d4ed8" }}>
                          Integration Setup Required
                        </span>
                      </div>
                      <p style={{ fontSize: "13px", color: "#1e40af", margin: 0 }}>
                        After completing your profile, you'll be redirected to connect your {profileData.calendarIntegration.type === 'google' ? 'Google' : 'Microsoft'} account for calendar synchronization.
                      </p>
                    </div>
                  )}
                </div>

                {/* Working Hours */}
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Standard Working Hours</label>
                  <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "16px" }}>
                    Set your typical availability. This can be synced with your calendar or used as default scheduling hours.
                  </p>
                  
                  <div style={{ backgroundColor: "#f8fafc", padding: "20px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                    {daysOfWeek.map((day) => (
                      <div key={day.key} style={styles.timeGrid} className="time-grid">
                        <div style={styles.dayLabel}>
                          <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              style={styles.checkbox}
                              checked={profileData.workingHours[day.key].enabled}
                              onChange={(e) => updateWorkingHours(day.key, 'enabled', e.target.checked)}
                            />
                            {day.label}
                          </label>
                        </div>
                        
                        <input
                          type="time"
                          style={{
                            ...styles.timeInput,
                            opacity: profileData.workingHours[day.key].enabled ? 1 : 0.5
                          }}
                          value={profileData.workingHours[day.key].start}
                          onChange={(e) => updateWorkingHours(day.key, 'start', e.target.value)}
                          disabled={!profileData.workingHours[day.key].enabled}
                        />
                        
                        <input
                          type="time"
                          style={{
                            ...styles.timeInput,
                            opacity: profileData.workingHours[day.key].enabled ? 1 : 0.5
                          }}
                          value={profileData.workingHours[day.key].end}
                          onChange={(e) => updateWorkingHours(day.key, 'end', e.target.value)}
                          disabled={!profileData.workingHours[day.key].enabled}
                        />
                      </div>
                    ))}
                  </div>
                  
                  <p style={{ fontSize: "12px", color: "#9ca3af", marginTop: "12px" }}>
                    💡 These hours will be used as your default availability. You can always override them through your connected calendar.
                  </p>
                </div>
              </>
            )}

            {/* Step 4: Services */}
            {currentStep === 4 && (
              <>
                <h2 style={styles.sectionTitle}>
                  <BookOpen style={{ width: "24px", height: "24px", color: "#f59e0b" }} />
                  Medical Services
                </h2>

                {/* Add Service Form */}
                <div style={styles.formGrid} className="form-grid">
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Service Title *</label>
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="General Consultation"
                      value={serviceForm.title}
                      onChange={(e) => setServiceForm({...serviceForm, title: e.target.value})}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Price (Optional)</label>
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="$150"
                      value={serviceForm.price}
                      onChange={(e) => setServiceForm({...serviceForm, price: e.target.value})}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Duration (Optional)</label>
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="30 minutes"
                      value={serviceForm.duration}
                      onChange={(e) => setServiceForm({...serviceForm, duration: e.target.value})}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Service Description *</label>
                  <textarea
                    style={styles.textarea}
                    placeholder="Describe this service, what it includes, and what patients can expect..."
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </div>

                <button
                  type="button"
                  style={styles.addButton}
                  className="add-button"
                  onClick={addService}
                  disabled={!serviceForm.title.trim() || !serviceForm.description.trim()}
                >
                  <Plus style={{ width: "16px", height: "16px" }} />
                  Add Service
                </button>

                {/* Services List */}
                {profileData.services.length > 0 && (
                  <div style={{ marginTop: "32px" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#111827", marginBottom: "16px" }}>
                      Your Services ({profileData.services.length})
                    </h3>
                    
                    {profileData.services.map((service) => (
                      <div key={service.id} style={styles.serviceCard}>
                        <div style={styles.serviceHeader}>
                          <div style={{ flex: 1 }}>
                            <h4 style={styles.serviceTitle}>{service.title}</h4>
                            {service.price && (
                              <span style={styles.servicePrice}>{service.price}</span>
                            )}
                          </div>
                          <button
                            style={{
                              background: "none",
                              border: "none",
                              color: "#ef4444",
                              cursor: "pointer",
                              padding: "4px",
                              borderRadius: "4px"
                            }}
                            onClick={() => removeService(service.id)}
                            title="Remove service"
                          >
                            <X style={{ width: "16px", height: "16px" }} />
                          </button>
                        </div>
                        
                        <p style={styles.serviceDescription}>{service.description}</p>
                        
                        {service.duration && (
                          <div style={styles.serviceMeta}>
                            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <Clock style={{ width: "12px", height: "12px" }} />
                              Duration: {service.duration}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {profileData.services.length === 0 && (
                  <div style={{
                    textAlign: "center",
                    padding: "48px 24px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "12px",
                    border: "2px dashed #e2e8f0",
                    marginTop: "24px"
                  }}>
                    <Stethoscope style={{ width: "48px", height: "48px", color: "#9ca3af", margin: "0 auto 16px auto" }} />
                    <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#374151", marginBottom: "8px" }}>
                      No services added yet
                    </h3>
                    <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>
                      Add your first medical service to let patients know what you offer
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Navigation Buttons */}
            <div style={styles.buttonRow} className="button-row">
              {currentStep > 1 && (
                <button
                  type="button"
                  style={{...styles.button, ...styles.secondaryButton}}
                  className="secondary-button"
                  onClick={() => setCurrentStep(currentStep - 1)}
                >
                  <ArrowRight style={{ width: "16px", height: "16px", transform: "rotate(180deg)" }} />
                  Previous
                </button>
              )}

              <div style={{ flex: 1 }}></div>

              {currentStep < 4 ? (
                <button
                  type="button"
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    ...(validateStep(currentStep) ? {} : { opacity: 0.5, cursor: "not-allowed" })
                  }}
                  className="primary-button"
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={!validateStep(currentStep)}
                >
                  Next
                  <ArrowRight style={{ width: "16px", height: "16px" }} />
                </button>
              ) : (
                <button
                  type="button"
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    ...(saving ? { opacity: 0.7, cursor: "not-allowed" } : {})
                  }}
                  className="primary-button"
                  onClick={saveProfile}
                  disabled={saving || !validateStep(4)}
                >
                  {saving ? (
                    <>
                      <div style={styles.spinner}></div>
                      Saving Profile...
                    </>
                  ) : (
                    <>
                      <Save style={{ width: "16px", height: "16px" }} />
                      Complete Profile
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorProfileSetup;